<template>
    <!-- Block -->
    <v-col
        class="d-flex flex-column fill-height"
        :class="[
            `theme--${theme}`,
            `content-block content-block-${block.id} content-block-${breakpointName}`,
            { 'content-block-border-top': block.dividerLine.top },
            { 'content-block-border-bottom': block.dividerLine.bottom },
            { 'content-block-border-start': block.dividerLine.start && breakpoint === 'desktop' },
            { 'content-block-border-end': block.dividerLine.end && breakpoint === 'desktop' },
            ...block.appearance.base.cssClass,
            ...block.appearance.theme[theme].cssClass,
            ...appearance.cssClass
        ]"
        :style="{
            ...block.appearance.base.cssStyle,
            ...block.appearance.theme[theme].cssStyle,
            ...appearance.cssStyle
        }"
    >
        <component
            v-if="background"
            :is="'style'"
        >
            {{ background }}
        </component>
        <slot name="default">
            <BtnUniversal
                :params="block.hyperlinkBlock"
                :disable-link="!block.hyperlinkBlock"
                class="container-grid content-block-items no-style-link pa-0 fill-width position-relative"
                :class="{
                    'container-grid--fluid': !block.constrainedWidth,
                    'fill-height': block.fillHeight || !isMobile
                }"
                nuxt-link
                plain-link
            >
                <v-row
                    class="fill-width flex-grow-0 flex-shrink-1"
                    :class="[
                        block.row.cssClass,
                        { 'fill-height': block.fillHeight || !isMobile },
                        ...appearanceRow.cssClass,
                        ...rowGridClasses
                    ]"
                    :style="{
                        ...block.row.cssStyle,
                        ...appearanceRow.cssStyle
                    }"
                    :density="block.row.dense ? 'compact' : 'default'"
                    :no-gutters="block.row.noGutters || true"
                >
                    <!-- Sub-Block -->
                    <ContentSubBlock
                        v-for="(subBlock, i) in block.blocks"
                        :key="i"
                        :id="`${block.id}`"
                        :sub-block="subBlock"
                        :theme="theme"
                        :breakpoint="breakpoint"
                        :breakpoint-name="breakpointName"
                        :is-mobile="isMobile"
                        :is-constrained-width="block.constrainedWidth"
                        :is-hyperlink-block="!!block.hyperlinkBlock"
                    ></ContentSubBlock>
                </v-row>
            </BtnUniversal>
        </slot>
    </v-col>
</template>

<script lang="ts" setup>
    import type {
        ContentBlockComponentProps,
        BreakpointType
    } from '~/types';
    import { ContentBlockThemeStyleItems } from '~/types';
    import BtnUniversal from '~/components/BtnUniversal/BtnUniversal.vue';
    import ContentSubBlock from '~/components/content/ContentSubBlock/ContentSubBlock.vue';
    import { legacyContentHelpers } from '~/composables';
    import {
        useCdnResource
    } from 'x1-base/composables';

    const props = withDefaults(
        defineProps<ContentBlockComponentProps>(),
        {
            theme: 'light',
            breakpoint: 'desktop',
            breakpointName: 'md'
        }
    );

    const useCdnResourceObj = useCdnResource();
    const { getAppearance, processBackground, getEmptyThemeStyleObj } = legacyContentHelpers;
    const blockClassName = `content-block-${props.block.id}`;
    const appearance = computed<ContentBlockThemeStyleItems>(() => {
        const current: BreakpointType = props.breakpointName;
        const appearance = props.block.appearance;

        return getAppearance(appearance, current);
    });

    const appearanceRow = computed<ContentBlockThemeStyleItems>(() => {
        const current: BreakpointType = props.breakpointName;
        const appearance = props.block.row.appearance;

        if (!appearance) {
            return getEmptyThemeStyleObj();
        }

        return getAppearance(appearance, current);
    });

    // v4: grid align/justify/align-content props are deprecated in favor of
    // utility classes. Build them dynamically from the CMS row config.
    const rowGridClasses = computed<string[]>(() => {
        const r = props.block.row;
        const classes: string[] = [];
        const add = (prefix: string, val: unknown) => {
            if (val) classes.push(`${prefix}-${val}`);
        };
        add('align', r.align);
        add('align-sm', r.alignSm);
        add('align-md', r.alignMd);
        add('align-lg', r.alignLg);
        add('align-xl', r.alignXl);
        add('align-content', r.alignContent);
        add('align-content-sm', r.alignContentSm);
        add('align-content-md', r.alignContentMd);
        add('align-content-lg', r.alignContentLg);
        add('align-content-xl', r.alignContentXl);
        add('justify', r.justify);
        add('justify-sm', r.justifySm);
        add('justify-md', r.justifyMd);
        add('justify-lg', r.justifyLg);
        add('justify-xl', r.justifyXl);
        return classes;
    });

    const background = computed<string>(() =>
        processBackground(blockClassName, props.block?.background, props.theme, useCdnResourceObj.cdnBasePathMain)
    );
</script>

<!-- Styles in /assets/styles/common.scss -->
<style lang="scss"></style>
